/**
 * Role-name predicates for the ABAC admin pages. The Settings page
 * (`/settings/abac`) gates on `isAbacAdmin`; the Platform page
 * (`/platform/abac`) gates on `isSuperAdmin`.
 *
 * Case-insensitive comparison — protects against the role-string casing
 * drift the peer review flagged for our brand-new ABAC_ADMIN role. The
 * server enforces real authorization via [AuthorizeAbac] / SuperAdmin
 * checks; these helpers are purely for UI gating + sidenav visibility.
 */

const SUPER_ADMIN = "SUPER_ADMIN";
const OWNER = "OWNER";
const ABAC_ADMIN = "ABAC_ADMIN";

const roleOf = (user) =>
  (user?.userDetails?.role || user?.role || "")
    .toString()
    .trim()
    .toUpperCase();

export const isSuperAdmin = (user) => roleOf(user) === SUPER_ADMIN;

export const isAbacAdmin = (user) => {
  const r = roleOf(user);
  return r === SUPER_ADMIN || r === OWNER || r === ABAC_ADMIN;
};

// Storage key for the super-admin tenant switcher. Per peer review §6,
// sessionStorage (not localStorage) so it auto-clears on tab close — kills
// the "walked away with another tenant loaded" risk on shared workstations.
export const ACTING_ORG_STORAGE_KEY = "abacActingOrgId";

export const getActingOrgId = () => {
  if (typeof window === "undefined" || !window.sessionStorage) return null;
  const raw = window.sessionStorage.getItem(ACTING_ORG_STORAGE_KEY);
  if (!raw) return null;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
};

export const setActingOrgId = (orgId) => {
  if (typeof window === "undefined" || !window.sessionStorage) return;
  if (orgId === null || orgId === undefined || orgId === 0 || orgId === "") {
    window.sessionStorage.removeItem(ACTING_ORG_STORAGE_KEY);
    return;
  }
  window.sessionStorage.setItem(ACTING_ORG_STORAGE_KEY, String(orgId));
};

export const clearActingOrgId = () => {
  if (typeof window === "undefined" || !window.sessionStorage) return;
  window.sessionStorage.removeItem(ACTING_ORG_STORAGE_KEY);
};
